import { Vendor } from '../vendor/vendor';
import { Admin } from '../admin/admin';
import { DateTime } from 'luxon';
import { Product } from '../product/product';
import { PaginationRequest } from '../pagination/pagination';
import { Invoice } from '../invoice/invoice';

export enum PurchaseOrderStatus {
  Requested = 'Requested',
  Approved = 'Approved',
  Received = 'Received',
  Cancelled = 'Cancelled',
}

export type PurchaseOrder = {
  id: string;
  locationId: number;
  vendor: Vendor;
  poNumber?: string;
  purchasedBy: Admin;
  purchasedAt: DateTime;
  products: PurchaseOrderProductLineItem[];
  paymentMethod?: PurchaseOrderPaymentMethod;
  subtotal: number;
  status: PurchaseOrderStatus;
  createdAt: DateTime;
  updatedAt: DateTime;
  invoices: Invoice[];
};

export type CreatePurchaseOrderRequest = {
  locationId: number;
  vendorId: number;
  purchasedById: number;
  purchasedAt: DateTime;
  paymentMethodId?: string;
  products: UpsertPurchaseOrderProductRequest[];
};

export type UpdatePurchaseOrderRequest = {
  poNumber?: string;
  paymentMethodId?: string;
  purchasedBy?: number;
  purchasedAt?: DateTime;
  products?: UpsertPurchaseOrderProductRequest[];
};

export type UpsertPurchaseOrderProductRequest = {
  id: string;
  productId: number;
  name?: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  generalLedgerAccountId?: string;
};

export type PurchaseOrderProductLineItem = {
  id: string;
  product: Product;
  name?: string;
  description?: string;
  quantity: number;
  stock?: PurchaseOrderProductStock;
  unitPrice: number;
  generalLedgerAccountId?: string;
};

export type PurchaseOrderProductStock = {
  id: string;
  purchaseOrderProductId: string;
  saleQuantity: number;
  rentalQuantity: number;
  createdAt: DateTime;
  createdBy: number;
  updatedAt: DateTime;
  updatedBy: number;
};

export type UpsertPurchaseOrderProductStock = {
  purchaseOrderProductId: string;
  saleQuantity: number;
  rentalQuantity: number;
};

export type PurchaseOrderProductInventoryUnit = {
  id: string;
  purchaseOrderProductId: string;
  unitNumber: string;
  serialNumber: string;
  qrCode?: string;
  hourReading?: number;
  modelId?: string;
  year?: number;
  forRent: boolean;
  forSale: boolean;
};

export type UpsertPurchaseOrderProductInventoryUnit = {
  unitNumber: string;
  serialNumber: string;
  qrCode?: string;
  hourReading?: number;
  modelYear?: { modelId: string; year: number };
  forRent: boolean;
  forSale: boolean;
};

export type UpsertPurchaseOrderProductInventoryUnits = {
  purchaseOrderProductId: string;
  units: UpsertPurchaseOrderProductInventoryUnit[];
};

export function toUpsertPurchaseOrderProductRequest(
  li: PurchaseOrderProductLineItem,
): UpsertPurchaseOrderProductRequest {
  return {
    id: li.id,
    productId: li.product.id,
    name: li.name,
    description: li.description,
    quantity: li.quantity,
    unitPrice: li.unitPrice,
    generalLedgerAccountId: li.generalLedgerAccountId,
  };
}

export type PurchaseOrderPaymentMethod = {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
};

export class PurchaseOrderSearchRequest extends PaginationRequest {
  keyword?: string;
  locationIds?: number[];
  vendorId?: number;
  purchasedById?: number;
  statuses?: PurchaseOrderStatus[];
  paymentMethodId?: string;
  startDate?: DateTime;
  endDate?: DateTime;
}

export type ReceivePurchaseOrderRequest = {
  products: (UpsertPurchaseOrderProductStock | UpsertPurchaseOrderProductInventoryUnits)[];
};
